import { useEffect, useState } from 'react';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ItemPickerField from '@/pages/mfg/ItemPickerField';
import { mktApi, errMsg } from './marketingApi';
import { CustomerPicker } from './mktShared';

const emptyLine = () => ({ line_id: null, item_id: null, item: null, qty: 1, fulfillment: 'production', notes: '' });

export default function OrderFormDialog({ open, onOpenChange, order = null, customers = [], onSaved }) {
  const [customerId, setCustomerId] = useState(null);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (order) {
      setCustomerId(order.customer_id);
      setTitle(order.title || '');
      setDueDate(order.due_date || '');
      setNotes(order.notes || '');
      setLines((order.lines || []).map((ln) => ({
        line_id: ln.line_id, item_id: ln.item_id,
        item: { id: ln.item_id, name: ln.name, sku: ln.sku, uom: ln.uom },
        qty: ln.qty, fulfillment: ln.fulfillment || 'production', notes: ln.notes || '',
      })));
    } else {
      setCustomerId(null); setTitle(''); setDueDate(''); setNotes('');
      setLines([emptyLine()]);
    }
  }, [open, order]);

  const setLine = (idx, patch) => setLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));

  const save = async () => {
    if (!customerId) { toast.error('客先を選択してください'); return; }
    if (!dueDate) { toast.error('希望納期を入力してください'); return; }
    const valid = lines.filter((ln) => ln.item_id && Number(ln.qty) > 0);
    if (!valid.length) { toast.error('少なくとも1件の明細（品目と数量）を入力してください'); return; }
    setSaving(true);
    try {
      const body = {
        customer_id: customerId, title, due_date: dueDate, notes,
        lines: valid.map((ln) => ({ line_id: ln.line_id || undefined, item_id: ln.item_id, qty: Number(ln.qty), fulfillment: ln.fulfillment, notes: ln.notes || null })),
      };
      const saved = order ? await mktApi.updateOrder(order.id, body) : await mktApi.createOrder(body);
      toast.success(order ? '受注を更新しました' : `受注 ${saved.order_no} を作成しました`);
      onSaved?.(saved);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mkt-order-form-dialog">
        <DialogHeader>
          <DialogTitle>{order ? `受注を編集 — ${order.order_no}` : '新規受注'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">客先 <span className="text-red-500">*</span></label>
              <div className="mt-1"><CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} testid="mkt-order-customer" /></div>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">希望納期 <span className="text-red-500">*</span></label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="mkt-order-due-date" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">件名</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例）○○装置用 部品一式" className="mt-1" data-testid="mkt-order-title" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-zinc-600">明細（品目マスタから選択） <span className="text-red-500">*</span></label>
              <Button type="button" variant="outline" size="sm" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="mkt-order-add-line-btn">
                <Plus size={13} className="mr-1" /> 行を追加
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <div key={idx} className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-2.5" data-testid={`mkt-order-line-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-12 sm:col-span-5">
                      <ItemPickerField value={ln.item_id} allowAllDepartments defaultAllMode
                        onChange={(id, item) => setLine(idx, { item_id: id, item })}
                        testid={`mkt-line-item-${idx}`} placeholderKey="mfg.wo.selectProduct" />
                    </div>
                    <div className="col-span-5 sm:col-span-2 flex items-center gap-1.5">
                      <Input type="number" min="0.01" step="any" value={ln.qty}
                        onChange={(e) => setLine(idx, { qty: e.target.value })}
                        className="h-10 text-right" data-testid={`mkt-line-qty-${idx}`} />
                      <span className="text-xs text-zinc-500 shrink-0">{ln.item?.uom || '個'}</span>
                    </div>
                    <div className="col-span-7 sm:col-span-2">
                      <Select value={ln.fulfillment} onValueChange={(v) => setLine(idx, { fulfillment: v })}>
                        <SelectTrigger className="h-10" data-testid={`mkt-line-fulfillment-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="production">製造</SelectItem>
                          <SelectItem value="stock">在庫出荷</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-10 sm:col-span-2">
                      <Input value={ln.notes} onChange={(e) => setLine(idx, { notes: e.target.value })} placeholder="備考" className="h-10" data-testid={`mkt-line-notes-${idx}`} />
                    </div>
                    <div className="col-span-2 sm:col-span-1 text-right">
                      <button type="button" onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                        className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`mkt-line-remove-${idx}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-400">品番の手入力はできません。品目マスタに登録済みの品目のみ選択できます。</p>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">備考</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="mkt-order-notes" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mkt-order-form-cancel-btn">キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-order-form-save-btn">
              {saving ? '保存中…' : (order ? '更新する' : '作成する')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
